import { Typography, Slider } from '@tunasong/ui-lib'
import type { HTMLAttributes } from 'react'
import { shortUuid } from '@tunasong/models'
import type { FC, MouseEvent } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { usePixelsPerSecond } from '../hooks/pixels-per-second.js'
import { plugins as allPlugins, loopPlugins } from './plugins.js'
import type { TimelineEvent } from './timeline-event.js'
import { getClickPositionSeconds } from './util.js'
import styles from './timeline.module.css'

export interface TimelineProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  className?: string

  duration: number
  // Pixels per second for the timeline, used to calculate the width of the timeline
  // If not provided, it will be calculated based on the container width
  pixelsPerSecond?: number
  height?: number
  events?: TimelineEvent[]

  loopOnly?: boolean

  playbackRate?: number
  onPlaybackRateChange?(playbackRate: number): void

  /** Click on the timeline at position in seconds */
  onClick?(ev: MouseEvent, seconds: number, lane?: number): void
  onEventClick?(ev: MouseEvent, event: TimelineEvent): void
  onUpdateEvent?(event: TimelineEvent): void
}

interface TimelineSvgProps extends React.SVGProps<SVGSVGElement> {
  containerHeight: number
}

const TimelineSvg = ({ containerHeight, ...props }: TimelineSvgProps) => (
  <svg {...props} style={{ width: '100%', height: containerHeight, ...props.style }} />
)

export const Timeline: FC<TimelineProps> = props => {
  const {
    className,
    duration,
    playbackRate = 1.0,
    onPlaybackRateChange,
    pixelsPerSecond: providedPps,
    onUpdateEvent,
    height = 36,
    events,
    onClick,
    onEventClick,
    loopOnly = false,
    ...restProps
  } = props

  // Height is passed directly to styled components

  const [canvas, setCanvas] = useState<SVGElement | null>(null)
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null)

  const lanes = loopOnly ? 1 : 4
  const plugins = loopOnly ? loopPlugins : allPlugins

  const laneHeight = Math.round(height / lanes)

  const containerPixelsPerSecond = usePixelsPerSecond(rootEl, duration)

  const pixelsPerSecond = providedPps ?? containerPixelsPerSecond

  const timeInterval = useMemo(() => {
    /** Max 1 interval per 50 pixels, rounding to 10 second intervals */
    const pixelsPerInterval = 50
    const roundTo = 10
    const interval = (duration * pixelsPerSecond) / pixelsPerInterval
    const timeInterval = Math.floor(duration / interval)
    const val = Math.max(Math.ceil(timeInterval / roundTo) * roundTo, 1)
    return Number.isSafeInteger(val) ? val : 10
  }, [duration, pixelsPerSecond])

  const getLane = useCallback(
    (ev: MouseEvent) => {
      const top = canvas?.getBoundingClientRect().top ?? 0
      const y = ev.clientY - top
      const lane = Math.floor(y / laneHeight) + 1.0
      return lane
    },
    [canvas, laneHeight]
  )

  const isDraggingRef = useRef(false)

  const timeline = useMemo(() => {
    if (!canvas || !Number.isFinite(pixelsPerSecond) || pixelsPerSecond <= 0) {
      return []
    }
    /**
     * Layout is as follows:
     *
     * 1/4 is used for time labels
     * 1/4 is used for notches
     * 1/2 is used for labels (if labels are specified)
     */

    const positionEvents: TimelineEvent[] = []
    let seconds = 0
    for (let index = 0; index < duration / timeInterval; index++) {
      positionEvents.push({ id: shortUuid(), type: 'position', start: seconds, name: 'Notch' })
      seconds += timeInterval
    }

    const allEvents = [...positionEvents, ...(events ?? [])]

    const noopFn = () => {}

    /** Process the positions with the plugins */
    const elements = allEvents
      .map((event, eventIdx) =>
        plugins.map((Plugin, pluginIdx) => (
          <Plugin
            key={`${event.id ?? eventIdx}-${event.type}-plugin-${pluginIdx}`}
            event={event}
            laneHeight={laneHeight}
            onDragStart={() => (isDraggingRef.current = true)}
            onDragEnd={() => (isDraggingRef.current = false)}
            pixelsPerSecond={pixelsPerSecond}
            onEventClick={onEventClick ?? noopFn}
            onUpdateEvent={onUpdateEvent ?? noopFn}
          />
        ))
      )
      .flat()
    return elements
  }, [canvas, pixelsPerSecond, events, duration, timeInterval, plugins, laneHeight, onEventClick, onUpdateEvent])

  const handleClick = (ev: MouseEvent) => {
    if (!onClick || isDraggingRef.current) {
      return
    }

    const seconds = getClickPositionSeconds(ev, { duration, pixelsPerSecond })
    const lane = getLane(ev)
    onClick(ev, seconds, lane)
  }

  const [showSlider, setShowSlider] = useState(false)

  if (duration <= 0 || !events || events.length === 0) {
    const message =
      duration <= 0 ? 'Play audio, then click timeline to create loops' : 'Click the timeline to create loops'
    return (
      <div
        className={cn(styles.emptyContainer, className)}
        data-testid="timeline"
        style={{ height, cursor: duration > 0 ? 'crosshair' : 'default' }}
        ref={setRootEl}
        onClick={duration > 0 ? handleClick : undefined}
        {...restProps}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          className={styles.emptyMessage}
        >
          {message}
        </Typography>
      </div>
    )
  }

  return (
    <div
      className={cn(styles.container, className)}
      data-testid="timeline"
      style={{ height, cursor: isDraggingRef.current ? 'grabbing' : 'crosshair' }}
      ref={setRootEl}
      draggable={false}
      {...restProps}
    >
      {/* Playback rate control as a small dropdown on the absolute right */}
      {onPlaybackRateChange && (
        <div className={styles.rateControl}>
          {/* Show only the label unless interacting */}
          <div
            className={styles.rateLabel}
            onClick={e => {
              e.stopPropagation()
              setShowSlider(true)
            }}
            tabIndex={0}
          >
            {`${playbackRate}x`}
          </div>
          {showSlider && (
            <div
              className={styles.sliderContainer}
              onMouseLeave={() => setShowSlider(false)}
            >
              <Slider
                aria-label="Playback rate"
                value={playbackRate}
                min={0.5}
                max={2}
                step={0.05}
                onChange={(_, value) => onPlaybackRateChange?.(value)}
                className={styles.slider}
                size="small"
              />
            </div>
          )}
        </div>
      )}

      <TimelineSvg ref={setCanvas} containerHeight={height} preserveAspectRatio="none" onClick={handleClick}>
        {timeline}
      </TimelineSvg>
    </div>
  )
}
